import { createAsyncThunk, nanoid } from '@reduxjs/toolkit';

import {
  deleteCurrentInsurance,
  deleteCurrentLicence,
  deleteCurrentSticker,
  deleteCurrentTransport,
  isEmptyDataGarageWithFilter,
  setCurrentTransport,
  setLoadingGarage,
} from '@pages/hubs/Garage/store/garage-slice';
import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import { extractInteriorColor } from '../utils/extractInteriorColor';

import type {
  CreateTransportFormType,
  GetTransportsParams,
  Insurances,
  Licences,
  ReminderTempData,
  ResultDataRecognitionData,
  ResultInfo,
  ResultResponseVehicleRegistrationData,
  RootGarageItemsData,
  RootMeta,
  UpdateInsurancesFormPayloadType,
  UpdateLicense,
  UpdateTransportFormPayloadType,
  UpdateVehicleSticker,
  VehicleStickers,
} from './types';
import type { Entity } from '../Garage';
import type { USState } from '../enum/UsaStateEnum';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { VinAndLicensePlateInfoType } from '@shared/models/garage/vin-info';
import type { OptionType } from '@shared/ui-kit/selects/MuiSelect/types';
import type { AppDispatch, RootState } from '@store/store';

export const getVinInfo = createAsyncThunk<
  ResultInfo<VinAndLicensePlateInfoType>,
  string,
  { rejectValue: ErrorType }
>('VinInfo', async (vin: string, { rejectWithValue }) => {
  try {
    return await api.garage.getVehicleInfoByVin(vin);
  } catch (err) {
    return rejectWithValue(err as ErrorType);
  }
});

export const getLicensePlateInfo = createAsyncThunk<
  ResultInfo<VinAndLicensePlateInfoType>,
  { licensePlate: string; state: USState },
  { rejectValue: ErrorType }
>('VinInfo', async ({ licensePlate, state }, { rejectWithValue }) => {
  try {
    return await api.garage.getVehicleInfoByLicensePlate(licensePlate, state);
  } catch (err) {
    return rejectWithValue(err as ErrorType);
  }
});

export const getInfoInterior = createAsyncThunk<
  ResultInfo<OptionType[]>,
  { make: string; model: string; year: string | number },
  { rejectValue: ErrorType }
>('getInfoInterior', async ({ year, make, model }, { rejectWithValue }) => {
  try {
    const res = await api.garage.getInterior({ model, make, year });

    const arrayMapper = !!res.data.length
      ? res.data.map((item) => ({
          id: nanoid(),
          value: extractInteriorColor(item.name),
          label: extractInteriorColor(item.name),
        }))
      : [];
    return {
      data: arrayMapper,
      status: res.status,
    };
  } catch (err) {
    return rejectWithValue(err as ErrorType);
  }
});

export const getInfoExterior = createAsyncThunk<
  ResultInfo<OptionType[]>,
  { make: string; model: string; year: string | number },
  { rejectValue: ErrorType }
>('getInfoExterior', async ({ year, make, model }, { rejectWithValue }) => {
  try {
    const res = await api.garage.getExterior({ make, model, year });
    const arrayMapper = !!res.data.length
      ? res.data.map((item) => ({ id: nanoid(), label: item.name, value: item.name }))
      : [];
    return {
      data: arrayMapper,
      status: res.status,
    };
  } catch (err) {
    return rejectWithValue(err as ErrorType);
  }
});

export const getInfoMake = createAsyncThunk<ResultInfo<OptionType[]>, string, { rejectValue: ErrorType }>(
  'InfoMake',
  async (vehicleType: string, { rejectWithValue }) => {
    try {
      const res = await api.garage.getMakeInfo(vehicleType);

      const arrayMapper = res.data.map((item: string) => ({ id: nanoid(), label: item, value: item }));
      return {
        data: arrayMapper,
        status: res.status,
      };
    } catch (err) {
      return rejectWithValue(err as ErrorType);
    }
  },
);

export const getInfoModel = createAsyncThunk<
  ResultInfo<OptionType[]>,
  { make: string; year: number; vehicleType: string },
  { rejectValue: ErrorType }
>('InfoModel', async (modelData, { rejectWithValue }) => {
  try {
    const res = await api.garage.getModelInfo(modelData.make, modelData.year, modelData.vehicleType);
    const arrayMapper = res.data.map((item: string) => ({ id: nanoid(), label: item, value: item }));
    return {
      data: arrayMapper,
      status: res.status,
    };
  } catch (err) {
    return rejectWithValue(err as any);
  }
});

export const getTransportsV2 = createAsyncThunk<
  { data: RootGarageItemsData[]; meta: RootMeta },
  Partial<GetTransportsParams>,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getTransportsV2', async (garageParams, { dispatch, rejectWithValue }) => {
  dispatch(setLoadingGarage({ isLoading: false }));
  try {
    const { data, meta } = await api.garage.getTransports({
      page: garageParams.page || 1,
      ...garageParams,
    });

    dispatch(isEmptyDataGarageWithFilter({ filters: garageParams, garageData: data }));

    return {
      data,
      meta,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoadingGarage({ isLoading: true }));
  }
});

export const createNewTransport = createAsyncThunk<
  RootGarageItemsData,
  CreateTransportFormType,
  { rejectValue: ErrorType }
>('createNewTransport', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.createNewTransport(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const saveDraftRecognizedData = createAsyncThunk<
  any,
  Partial<VinAndLicensePlateInfoType> & Partial<ReminderTempData>,
  { rejectValue: ErrorType }
>('saveDraftRecognizedData', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.saveDraftRecognizedData(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getDraftRecognizedData = createAsyncThunk<any, { id: number }, { rejectValue: ErrorType }>(
  'getDraftRecognizedData',
  async (data, { rejectWithValue }) => {
    try {
      return await api.garage.getDraftRecognizedData(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const createInsurances = createAsyncThunk<
  Insurances,
  UpdateInsurancesFormPayloadType,
  { rejectValue: ErrorType }
>('createInsurances', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.createInsurances(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const createLicense = createAsyncThunk<Licences, Partial<UpdateLicense>, { rejectValue: ErrorType }>(
  'createLicense',
  async (data, { rejectWithValue }) => {
    try {
      return await api.garage.createLicensePlate(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data as ErrorType);
    }
  },
);

export const createVehicleSticker = createAsyncThunk<
  VehicleStickers,
  Partial<UpdateVehicleSticker>,
  { rejectValue: ErrorType }
>('createVehicleSticker', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.createVehicleSticker(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateVehicleSticker = createAsyncThunk<
  VehicleStickers,
  Partial<UpdateVehicleSticker> & { vehicleStickerID: number; status?: number },
  { rejectValue: ErrorType }
>('updateVehicleSticker', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.updateVehicleSticker(data, data.vehicleStickerID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateLicense = createAsyncThunk<
  Licences,
  Partial<UpdateLicense> & { licenceID: number; status?: number },
  { rejectValue: ErrorType }
>('updateLicense', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.updateLicensePlate(data, data.licenceID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateInsurances = createAsyncThunk<
  Insurances,
  UpdateInsurancesFormPayloadType & { insurancesId: number; status?: number },
  { rejectValue: ErrorType }
>('updateInsurances', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.updateInsurances(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});

export const updateTransport = createAsyncThunk<
  RootGarageItemsData,
  Partial<UpdateTransportFormPayloadType>,
  { rejectValue: ErrorType }
>('updateTransport', async (data, { rejectWithValue }) => {
  try {
    return await api.garage.updateTransport(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const publishTransport = createAsyncThunk<any, { transportId: number }, { rejectValue: ErrorType }>(
  'updateTransport',
  async (data, { rejectWithValue }) => {
    try {
      return await api.garage.publishTransport(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const deleteTransport = createAsyncThunk<any, { transportID: number }, { rejectValue: ErrorType }>(
  'deleteTransport',
  async ({ transportID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.garage
        .deleteTransport(transportID)
        .then(() => {
          dispatch(deleteCurrentTransport({ transportID }));
          NotificationService.success(i18next.t('general.notifications.transportDeleted'));
        })
        .catch(() => {
          NotificationService.error(i18next.t('general.notifications.defaultError'));
        });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data as ErrorType);
    }
  },
);

export const deleteInsurance = createAsyncThunk<
  any,
  { transportID: number; insuranceID: number },
  { rejectValue: ErrorType }
>('deleteInsurance', async ({ transportID, insuranceID }, { rejectWithValue, dispatch }) => {
  try {
    return await api.garage.deleteInsurance(transportID, insuranceID).then((res) => {
      dispatch(deleteCurrentInsurance({ insuranceID }));
      NotificationService.success(i18next.t('general.notifications.insuranceDeleted'));
      return res;
    });
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const deleteLicence = createAsyncThunk<any, { licenceID: number }, { rejectValue: ErrorType }>(
  'deleteLicence',
  async ({ licenceID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.garage.deleteLicence(licenceID).then((res) => {
        dispatch(deleteCurrentLicence({ licenceID }));
        NotificationService.success(i18next.t('general.notifications.vehicleStickerDeleted'));
        return res;
      });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const deleteVehicleSticker = createAsyncThunk<any, { stickerID: number }, { rejectValue: ErrorType }>(
  'deleteVehicleSticker',
  async ({ stickerID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.garage.deleteSticker(stickerID).then((res) => {
        dispatch(deleteCurrentSticker({ stickerID }));
        NotificationService.success(i18next.t('general.notifications.licensePlateStickerDeleted'));
        return res;
      });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const scanningInsuranceCard = createAsyncThunk<
  ResultDataRecognitionData | any,
  number,
  { rejectValue: ErrorType }
>('scanningInsuranceCard', async (id, { rejectWithValue }) => {
  try {
    return await api.garage.uploadInsurance(id);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(e as ErrorType);
  }
});
export const scanningVehicleRegistrationCard = createAsyncThunk<
  ResultResponseVehicleRegistrationData,
  number,
  { rejectValue: ErrorType }
>('scanningInsuranceCard', async (mediaID, { rejectWithValue }) => {
  try {
    return await api.garage.scanningVehicleRegistration(mediaID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(
      e,
      {},
      {
        notify: ![StatusServerErrors.BadRequest, StatusServerErrors.ValidationException].includes(
          axiosError.status,
        ),
      },
    );

    return rejectWithValue(e as ErrorType);
  }
});

export const getCurrentTransport = createAsyncThunk<RootGarageItemsData, number, { rejectValue: ErrorType }>(
  'getCurrentTransport',
  async (transportID, { rejectWithValue, dispatch }) => {
    try {
      const transport = await api.garage.getTransport(transportID);
      dispatch(setCurrentTransport({ transport }));
      return transport;
    } catch (e) {
      const axiosError = e as ServerErrorType;

      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('general.notifications.transportNotAvailable'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('general.notifications.transportNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);
export const getCurrentInsurance = createAsyncThunk<
  Insurances,
  { insuranceId: number; transportId: number },
  { rejectValue: ErrorType }
>('getCurrentInsurance', async ({ insuranceId, transportId }, { rejectWithValue }) => {
  try {
    return await api.garage.getInsurance(insuranceId, transportId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);

    return rejectWithValue(axiosError.data);
  }
});
export const getInsurances = createAsyncThunk<
  Insurances[],
  {
    policyNumber?: string | null;
    is_has_dates?: number;
  },
  { rejectValue: ErrorType }
>('getInsurances', async (params, { rejectWithValue }) => {
  try {
    const insurances = await api.garage.getInsurances(params);

    return insurances.data;
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getCurrentLicence = createAsyncThunk<
  Licences,
  { licenceId: number },
  { rejectValue: ErrorType }
>('getCurrentLicence', async ({ licenceId }, { rejectWithValue }) => {
  try {
    return await api.garage.getLicence(licenceId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getCurrentVehicleSticker = createAsyncThunk<
  VehicleStickers,
  { vehicleId: number },
  { rejectValue: ErrorType }
>('getCurrentVehicleSticker', async ({ vehicleId }, { rejectWithValue }) => {
  try {
    return await api.garage.getVehicleSticker(vehicleId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getDraftTransport = createAsyncThunk<RootGarageItemsData, number, { rejectValue: ErrorType }>(
  'getDraftTransport',
  async (transportID, { rejectWithValue }) => {
    try {
      return await api.garage.getTransport(transportID);
    } catch (e) {
      const axiosError = e as ServerErrorType;

      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('general.notifications.transportNotAvailable'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('general.notifications.transportNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateTransportWS = createAsyncThunk<
  {
    updateTransportWS: RootGarageItemsData;
  },
  Entity,
  { rejectValue: ErrorType }
>('updateTransport', async (socketEvent, { rejectWithValue }) => {
  try {
    const updateTransportResponse = await api.garage.getTransport(socketEvent.entity_id);

    return {
      updateTransportWS: updateTransportResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const addTransportWS = createAsyncThunk<
  {
    addTransportWS: RootGarageItemsData;
  },
  Entity,
  { rejectValue: ErrorType }
>('addTransportWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const addTransportResponse = await api.garage.getTransport(socketEvent.entity_id);

    return {
      addTransportWS: addTransportResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const acceptedTransportWS = createAsyncThunk<
  {
    acceptedTransportWS: RootGarageItemsData;
  },
  number,
  { rejectValue: ErrorType }
>('acceptedTransportWS', async (transportID, { rejectWithValue }) => {
  try {
    const acceptedTransportResponse = await api.garage.getTransport(transportID);

    return {
      acceptedTransportWS: acceptedTransportResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
